import { deleteData, getData, postData, putData } from "../api/api";
import type { PageResponse } from "../types/dashboard";

export interface GuideDraft {
  id?: string;
  indexName?: string;
  draftTitle?: string;
  draftMarkdown?: string;
  mappingHash?: string;
  fieldCount?: number;
  status?: "DRAFT" | "APPROVED" | "REJECTED" | "INGESTING" | "INGESTED";
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  guideDocumentId?: string;
  analysisText?: string;
  analysisApprovedBy?: string;
  analysisApprovedAt?: string;
}

export interface GuideDraftAnalysis {
  analysisText: string;
  indexName: string;
  draftTitle: string;
  status: string;
}

export interface MappingDiscoveryResponse {
  draftId?: string;
  indexName?: string;
  mappingHash?: string;
  fieldCount?: number;
  draftTitle?: string;
  draftMarkdown?: string;
  fields?: Array<{
    path: string;
    type: string;
    aggregatable: boolean;
    notes?: string;
  }>;
}

export const mappingDiscoveryService = {
  discover(indexName: string) {
    return postData("/admin/mapping/discover", { indexName }) as Promise<MappingDiscoveryResponse>;
  },
  listDrafts(page = 0, size = 20) {
    return getData(`/admin/mapping/drafts?page=${page}&size=${size}`) as Promise<PageResponse<GuideDraft>>;
  },
  getDraft(id: string) {
    return getData(`/admin/mapping/drafts/${id}`) as Promise<GuideDraft>;
  },
  getAnalysis(id: string) {
    return getData(`/admin/mapping/drafts/${id}/analysis`) as Promise<GuideDraftAnalysis>;
  },
  transferToRag(id: string) {
    return postData(`/admin/mapping/drafts/${id}/transfer-to-rag`, {}) as Promise<GuideDraft>;
  },
  approve(id: string) {
    return postData(`/admin/mapping/drafts/${id}/transfer-to-rag`, {}) as Promise<GuideDraft>;
  },
  deleteIngested(id: string) {
    return deleteData(`/admin/mapping/drafts/${id}/ingested`) as Promise<GuideDraft>;
  },
  deleteRejected(id: string) {
    return deleteData(`/admin/mapping/drafts/${id}/rejected`) as Promise<void>;
  },
  generateAnalysis(id: string) {
    return postData(`/admin/mapping/drafts/${id}/analysis/generate`, {}) as Promise<{ analysisText: string }>;
  },
  updateAnalysis(id: string, analysisText: string) {
    return putData(`/admin/mapping/drafts/${id}/analysis`, { analysisText }) as Promise<{ analysisText: string }>;
  },
  approveAnalysis(id: string, analysisText?: string) {
    return postData(
      `/admin/mapping/drafts/${id}/analysis/approve`,
      analysisText ? { analysisText } : {}
    ) as Promise<GuideDraft>;
  },
  reject(id: string, reason?: string) {
    return postData(`/admin/mapping/drafts/${id}/reject`, reason ? { reason } : {}) as Promise<GuideDraft>;
  },
  checkChanges(autoCreateDraft = false) {
    return postData(
      `/admin/mapping/check-changes?autoCreateDraft=${autoCreateDraft}`,
      {}
    ) as Promise<
      Array<{
        indexName: string;
        storedMappingHash?: string;
        currentMappingHash?: string;
        latestDraftId?: string;
        draftCreated?: boolean;
      }>
    >;
  },
};
